/**
 * UpgradeOverlay Component
 * Overlay displayed to encourage free plan users to upgrade
 */

import React from 'react';
import { Zap, Lock, ArrowRight, Crown } from 'lucide-react';

export default function UpgradeOverlay({
  feature = "this feature",
  showOverlay = true,
  compact = false
}) {

  if (!showOverlay) return null;

  if (compact) {
    return (
      <div className="flex items-center justify-center gap-2 p-4 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
        <Lock className="w-5 h-5 text-orange-600" />
        <span className="text-sm text-gray-700">
          <strong>Plan Pro requis</strong> pour {feature}
        </span>
        <button
          onClick={() => window.location.href = 'https://sylink-pentest.com/pricing'}
          className="ml-auto px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-red-600 transition-all flex items-center gap-2"
        >
          <Zap className="w-4 h-4" />
          Passer au Pro
        </button>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm rounded-lg">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 text-center">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
          <Crown className="w-8 h-8 text-white" />
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Premium Feature
        </h3>

        {/* Message */}
        <p className="text-gray-600 mb-6">
          Access {feature} starting from the{' '}
          <span className="font-semibold text-orange-600">Pro Plan</span>.
          Upgrade to a higher plan to unlock all security features.
        </p>

        {/* Features list */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm font-semibold text-gray-700 mb-2">With the Pro Plan, access:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              Unlimited security scans
            </li>
            <li className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              Advanced OSINT techniques
            </li>
            <li className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              Data enrichment
            </li>
            <li className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              Analyses IA et rapports
            </li>
          </ul>
        </div>

        {/* CTA Button */}
        <a
          href="https://sylink-pentest.com/pricing"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all flex items-center justify-center gap-2"
        >
          <Zap className="w-5 h-5" />
          Passer au Plan Pro
          <ArrowRight className="w-5 h-5" />
        </a>

        {/* Secondary text */}
        <p className="text-xs text-gray-500 mt-4">
          From €299/month · No commitment
        </p>
      </div>
    </div>
  );
}

/**
 * Badge variant - to display a "Pro" badge next to a feature
 */
export function ProBadge({ className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold rounded ${className}`}>
      <Crown className="w-3 h-3" />
      PRO
    </span>
  );
}

/**
 * Disabled button wrapper - to visually disable a button
 */
export function DisabledActionButton({ children, onClick, className = "", tooltip = "Plan Pro requis" }) {
  return (
    <div className="relative group">
      <button
        disabled
        className={`${className} opacity-50 cursor-not-allowed`}
        title={tooltip}
      >
        {children}
      </button>
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
        {tooltip}
        <ProBadge className="ml-2" />
      </div>
    </div>
  );
}
